import { useSessionUser } from "hooks/useSession";
import { useCallback, useEffect, useMemo, useState, type FC, type ReactNode } from "react";
import { Title as DialogTitle } from "@radix-ui/react-dialog";
import {
  FaRocket,
  FaPenNib,
  FaLayerGroup,
  FaCalendarAlt,
  FaDollarSign,
  FaUsers,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { Button } from "components/common/buttons/Button";
import { EmbeddedStudioPurchaseModal } from "components/common/EmbeddedStudioPurchaseModal";
import { Modal, ModalBody } from "components/common/modal";
import { CUSTOMER_SUCCESS_EMAIL, CUSTOMER_SUCCESS_MAILTO, PATH_DASHBOARD } from "config";
import { catalogKeys, sessionKeys } from "queries/profile.keys";
import { useUpgradeProject } from "hooks/useProjects";
import { usePurchaseLicense } from "hooks/usePurchaseLicense";
import { useProjectsApi } from "hooks/useProjects";
import {
  useOtpProducts,
  resolveDefaultWorkspaceOtpProduct,
  type OtpProductCheckoutRow,
} from "hooks/project";

interface PurchaseProjectModalProps {
  open: boolean;
  onClose: () => void;
  mode?: "trial" | "purchase" | "auto" | "activeTrial" | "licenseProject" | "upgradeTrial";
  projectId?: string;
  onCreateNewProject?: () => void;
}

interface UpgradeFeatureGroup {
  id: string;
  label: string;
  icon: ReactNode;
  summary: string;
  studioOnly?: boolean;
}

const WRITE_FEATURE: UpgradeFeatureGroup = {
  id: "write",
  label: "Write",
  icon: <FaPenNib />,
  summary: "Unlimited scenes, full version history, automatic revision pages and locked drafts.",
};

const BREAKDOWN_FEATURE: UpgradeFeatureGroup = {
  id: "breakdown",
  label: "Break down",
  icon: <FaLayerGroup />,
  summary: "AI scene & character breakdowns, prop and wardrobe lists, coverage and notes.",
};

const SCHEDULE_FEATURE: UpgradeFeatureGroup = {
  id: "schedule",
  label: "Schedule",
  icon: <FaCalendarAlt />,
  summary: "AI shooting schedules, day-out-of-days, cast and location grouping.",
};

const BUDGET_FEATURE: UpgradeFeatureGroup = {
  id: "budget",
  label: "Budget",
  icon: <FaDollarSign />,
  summary: "AI budgets with guild rates, line items, min/max scenarios and exportable reports.",
};

const COLLABORATE_FEATURE: UpgradeFeatureGroup = {
  id: "collaborate",
  label: "Collaborate & deliver",
  icon: <FaUsers />,
  summary:
    "Invite collaborators, share read-only links, leave comments and export to FDX / PDF / CSV.",
};

const ALL_FEATURE_GROUPS: UpgradeFeatureGroup[] = [
  WRITE_FEATURE,
  BREAKDOWN_FEATURE,
  { ...SCHEDULE_FEATURE, studioOnly: true },
  { ...BUDGET_FEATURE, studioOnly: true },
  COLLABORATE_FEATURE,
];

function formatUsd(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

export const PurchaseProjectModal: FC<PurchaseProjectModalProps> = ({
  open,
  onClose,
  mode = "purchase",
  projectId,
  onCreateNewProject,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { session: profile, isOneTimePurchase } = useSessionUser();
  const { projects } = useProjectsApi();
  const pricingPlanId = profile?.pricing_plan_id ?? null;
  const subscription = profile?.subscription;

  const isUpgradeMode = mode === "licenseProject" || mode === "upgradeTrial";

  const {
    upgradeProject,
    isUpgrading,
    canUpgrade,
    availableLicenses,
    isCheckingEligibility,
    eligibilityMessage,
  } = useUpgradeProject(open ? projectId : undefined);

  const {
    data: otpProducts = [],
    isLoading: otpProductsLoading,
    isError: otpProductsError,
    refetch: refetchOtpProducts,
  } = useOtpProducts(open && !isUpgradeMode);

  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);

  useEffect(() => {
    if (!otpProducts.length) return;
    setSelectedProductId((prev) => {
      if (prev != null && otpProducts.some((p) => p.id === prev)) {
        return prev;
      }
      const studio =
        resolveDefaultWorkspaceOtpProduct(otpProducts) ??
        otpProducts.find((p) => p.tier === "studio");
      return studio?.id ?? otpProducts[0]!.id;
    });
  }, [otpProducts]);

  const selectedProduct = useMemo((): OtpProductCheckoutRow | null => {
    if (selectedProductId == null) return null;
    return otpProducts.find((p) => p.id === selectedProductId) ?? null;
  }, [otpProducts, selectedProductId]);

  const handlePurchaseSuccess = useCallback(() => {
    onClose();
  }, [onClose]);

  const {
    purchaseLicense,
    isLoading: isPurchasing,
    embeddedSession,
    dismissEmbeddedCheckout,
    onEmbeddedPurchaseComplete,
  } = usePurchaseLicense({
    context: isUpgradeMode ? "upgrade_project" : "project_creation",
    projectId,
    onSuccess: handlePurchaseSuccess,
  });

  useEffect(() => {
    if (!open) {
      dismissEmbeddedCheckout();
    }
  }, [open, dismissEmbeddedCheckout]);

  const hasActiveSubscription =
    !isOneTimePurchase && pricingPlanId !== null && subscription?.status === "active";

  useEffect(() => {
    if (open && hasActiveSubscription) {
      onClose();
      navigate(PATH_DASHBOARD.createProject);
    }
  }, [open, hasActiveSubscription, navigate, onClose]);

  useEffect(() => {
    if (open) {
      queryClient.invalidateQueries({ queryKey: sessionKeys.all });
      queryClient.invalidateQueries({ queryKey: catalogKeys.all });
    }
  }, [open, queryClient]);

  const currentProject = useMemo(
    () => (projectId ? projects.find((p) => String(p.projectId) === String(projectId)) : undefined),
    [projects, projectId]
  );

  const handleApplyLicense = useCallback(async () => {
    if (canUpgrade && upgradeProject && projectId) {
      await upgradeProject.mutateAsync(projectId);
    }
  }, [canUpgrade, upgradeProject, projectId]);

  const handleBuyLicense = useCallback(async () => {
    if (selectedProductId == null) return;
    await purchaseLicense({ one_time_product_id: selectedProductId });
  }, [purchaseLicense, selectedProductId]);

  const sortedProducts = useMemo(() => {
    const tierOrder: Record<string, number> = { writer: 0, studio: 1 };
    return [...otpProducts].sort(
      (a, b) => (tierOrder[a.tier] ?? 9) - (tierOrder[b.tier] ?? 9) || a.price - b.price
    );
  }, [otpProducts]);

  if (!open || hasActiveSubscription) {
    return null;
  }

  const projectNameDisplay = currentProject?.name?.trim() || "this project";
  const canApplyExistingLicense = Boolean(projectId) && canUpgrade;
  // Upgrade mode loads no products, so without an applicable license the button has nothing to do.
  const isUpgradeBlocked = isUpgradeMode && !canApplyExistingLicense;
  const showCreateAlternative = canApplyExistingLicense && Boolean(onCreateNewProject);

  const activeTier: "writer" | "studio" = isUpgradeMode
    ? "studio"
    : (selectedProduct?.tier ?? "studio");
  const isWriterSelected = !isUpgradeMode && activeTier === "writer";
  const heroTitle = isUpgradeMode ? "Upgrade this project" : "Activate this project";

  const primaryAction = canApplyExistingLicense
    ? {
        label:
          availableLicenses > 1
            ? `Apply available license (${availableLicenses} left)`
            : "Apply available license",
        onClick: handleApplyLicense,
        isLoading: isUpgrading,
      }
    : {
        label: isUpgradeMode ? "Upgrade this project" : "Buy license",
        onClick: handleBuyLicense,
        isLoading: isUpgradeBlocked ? isCheckingEligibility : isPurchasing,
      };

  const isAnyActionInFlight = isUpgrading || isPurchasing;
  const purchaseDisabled =
    isAnyActionInFlight ||
    isUpgradeBlocked ||
    (!canApplyExistingLicense &&
      !isUpgradeMode &&
      (otpProductsLoading || selectedProductId == null || !selectedProduct));

  return (
    <>
      <Modal
        isOpen={open}
        onClose={onClose}
        backdropClassName="bg-black/70 backdrop-blur-md"
        className="bg-transparent border-none shadow-none p-0"
      >
        <ModalBody className="p-0 bg-transparent">
          <div className="flex justify-center items-start sm:items-center min-h-screen py-4 sm:py-6">
            <div
              className="relative w-full max-w-[42rem] overflow-hidden rounded-2xl border border-border bg-gradient-to-b from-card to-background shadow-2xl shadow-black/60 animate-in fade-in-0 zoom-in-95 duration-300"
              role="dialog"
              aria-modal="true"
              aria-labelledby="purchase-project-modal-title"
            >
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -top-32 left-1/2 h-64 w-64 -translate-x-1/2 rounded-full bg-primary/25 blur-3xl"
              />

              <Button
                onClick={onClose}
                color="gray"
                size="icon"
                className="absolute top-3 right-3 z-10 h-7 w-7 rounded-full"
                aria-label="Close upgrade dialog"
                type="button"
              >
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </Button>

              <div className="relative flex flex-col items-center gap-3 px-6 pt-8 pb-6 text-center">
                <div className="relative">
                  <div
                    aria-hidden="true"
                    className="absolute inset-0 rounded-2xl bg-primary/20 blur-xl"
                  />
                  <div className="relative flex h-14 w-14 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10 text-primary">
                    <FaRocket className="-rotate-45 translate-y-px text-xl" />
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <DialogTitle
                    id="purchase-project-modal-title"
                    className="text-lg font-semibold leading-tight text-foreground sm:text-xl"
                  >
                    {heroTitle}
                  </DialogTitle>
                  <p className="text-sm leading-snug text-muted-foreground">
                    {isUpgradeMode ? (
                      <>
                        Unlock scheduling, budgeting, and full production AI for{" "}
                        <span className="font-medium text-primary">{projectNameDisplay}</span>.
                      </>
                    ) : (
                      <>
                        Unlock{" "}
                        <span className="font-medium text-primary">{projectNameDisplay}</span> with
                        a one-time Writer or Studio license.
                      </>
                    )}
                  </p>
                </div>
              </div>

              {!isUpgradeMode && !canApplyExistingLicense ? (
                <div className="px-6 pb-4">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {otpProductsLoading ? (
                      <>
                        <div className="h-24 animate-pulse rounded-xl border border-border bg-muted/50" />
                        <div className="h-24 animate-pulse rounded-xl border border-border bg-muted/50" />
                      </>
                    ) : otpProductsError ? (
                      <div className="col-span-full flex flex-col items-center gap-3 rounded-xl border border-border bg-muted/50 px-4 py-6 text-center">
                        <p className="text-sm text-muted-foreground">
                          Could not load license options. Check your connection and try again.
                        </p>
                        <Button
                          type="button"
                          color="gray"
                          onClick={() => void refetchOtpProducts()}
                        >
                          Retry
                        </Button>
                      </div>
                    ) : sortedProducts.length === 0 ? (
                      <div className="col-span-full flex flex-col items-center gap-3 rounded-xl border border-border bg-muted/50 px-4 py-6 text-center">
                        <p className="text-sm text-muted-foreground">
                          No Writer or Studio licenses are available to purchase right now.
                        </p>
                        <Button
                          type="button"
                          color="gray"
                          onClick={() => void refetchOtpProducts()}
                        >
                          Refresh
                        </Button>
                      </div>
                    ) : (
                      sortedProducts.map((p) => {
                        const selected = p.id === selectedProductId;
                        return (
                          <button
                            key={p.id}
                            type="button"
                            aria-pressed={selected}
                            onClick={() => setSelectedProductId(p.id)}
                            className={`flex flex-col gap-2 rounded-xl border px-4 py-3 text-left transition ${
                              selected
                                ? "border-primary/50 bg-primary/10 ring-1 ring-primary/30"
                                : "border-border bg-muted/50 hover:border-border"
                            }`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-sm font-semibold text-foreground">
                                {p.name}
                              </span>
                              <span className="text-sm font-bold text-primary">
                                {formatUsd(p.price)}
                              </span>
                            </div>
                            <div className="flex flex-col gap-0.5 text-xs text-muted-foreground">
                              <span>{p.included_ai_credits} AI credits</span>
                              <span>{p.max_team_members + 1} seats (incl. owner)</span>
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              ) : null}

              <div className="border-t border-border px-6 pb-6 pt-4">
                <ul className="flex flex-col gap-3">
                  {ALL_FEATURE_GROUPS.map((group) => {
                    const isLocked = isWriterSelected && group.studioOnly === true;
                    return (
                      <li key={group.id} className="flex items-start gap-3">
                        <div
                          className={
                            isLocked
                              ? "mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border bg-muted/50 text-[13px] text-muted-foreground/50"
                              : "mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-primary/20 bg-primary/10 text-[13px] text-primary"
                          }
                        >
                          {group.icon}
                        </div>
                        <div className="flex min-w-0 flex-col gap-0.5">
                          <div className="flex min-h-[1.375rem] flex-wrap items-center gap-1.5">
                            <span
                              className={
                                isLocked
                                  ? "text-sm font-medium leading-tight text-muted-foreground/70"
                                  : "text-sm font-medium leading-tight text-foreground"
                              }
                            >
                              {group.label}
                            </span>
                            {group.studioOnly ? (
                              <span
                                className={`inline-flex shrink-0 items-center rounded border border-primary/25 bg-primary/10 px-1 py-px text-[9px] font-semibold uppercase leading-none tracking-wide text-primary ${
                                  isLocked ? "visible" : "invisible"
                                }`}
                                aria-hidden={!isLocked}
                              >
                                Studio
                              </span>
                            ) : null}
                          </div>
                          <span
                            className={
                              isLocked
                                ? "text-xs leading-snug text-muted-foreground/50"
                                : "text-xs leading-snug text-muted-foreground"
                            }
                          >
                            {group.summary}
                          </span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>

              <div className="relative border-t border-border bg-muted/60 px-6 py-5">
                <div className="mb-4 flex items-center justify-between gap-3">
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-3xl font-bold leading-none text-foreground">
                      {isUpgradeMode
                        ? "—"
                        : selectedProduct
                          ? formatUsd(selectedProduct.price)
                          : "—"}
                    </span>
                    <span className="text-sm text-muted-foreground">one-time</span>
                  </div>
                  <span className="text-right text-[11px] leading-tight text-muted-foreground">
                    No subscription
                    <br />
                    Yours forever
                  </span>
                </div>

                <Button
                  type="button"
                  onClick={primaryAction.onClick}
                  disabled={purchaseDisabled}
                  isLoading={primaryAction.isLoading}
                  aria-busy={primaryAction.isLoading || undefined}
                  className="h-11 w-full font-semibold"
                >
                  {primaryAction.label}
                </Button>

                {isUpgradeBlocked && !isCheckingEligibility ? (
                  <p className="mt-3 text-center text-xs text-muted-foreground">
                    {eligibilityMessage || (
                      <>
                        This project cannot be upgraded right now. Please contact{" "}
                        <a
                          href={CUSTOMER_SUCCESS_MAILTO}
                          className="underline hover:text-foreground"
                        >
                          {CUSTOMER_SUCCESS_EMAIL}
                        </a>
                        .
                      </>
                    )}
                  </p>
                ) : null}

                {showCreateAlternative && (
                  <Button
                    type="button"
                    color="gray"
                    size="xs"
                    onClick={onCreateNewProject}
                    disabled={isAnyActionInFlight}
                    className="mt-3 w-full"
                  >
                    Start a different project instead
                  </Button>
                )}
              </div>
            </div>
          </div>
        </ModalBody>
      </Modal>
      {embeddedSession ? (
        <EmbeddedStudioPurchaseModal
          isOpen
          clientSecret={embeddedSession.clientSecret}
          publishableKey={embeddedSession.publishableKey}
          onClose={dismissEmbeddedCheckout}
          onComplete={onEmbeddedPurchaseComplete}
        />
      ) : null}
    </>
  );
};
